import { useAuthContext } from "../../auth/state/AuthContext";
import { themeAtom } from "../../shared/atoms";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { CommentModalProps, pnlReviewStatus, postReviewPayload } from "../models/ApproveRejectCommentBox.model";
import { RouteParams } from "../models/VoyagePnl.model";
import { postVoyagePnlReviewComments } from "../services/pnl.service";
import { cleanupVoyageLocalState, PnlContext } from "../utils/UtilFunctions";
import { ReusableButton } from "./ReusableTableButton";
import BlurredAlert from "./WarningMessage";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const ApproveRejectCommentBox: React.FC<CommentModalProps> = ({ onCancel, approved }) => {
  const theme = useAtomValue(themeAtom);

  const [comment, setComment] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { tcCode, voyageNo } = useParams<RouteParams>();
  const voyagePnlData = useAtomValue(PnlContext);
  const navigate = useNavigate();
  const authContext = useAuthContext();

  const reviewedBy = authContext?.state.user?.accessToken.sub || "Unknown reviewer";

  const vesselName = voyagePnlData?.voyageParams?.vesselName || "Unknown Vessel";
  const imoNumber = voyagePnlData?.voyageParams?.imoNumber || "Unknown imoNumber";

  const status: pnlReviewStatus = approved ? "Approved" : "Rejected";

  const handleSubmit = async () => {
    if (submitting) return;
    log("ApproveRejectCommentBox - handleSubmit", { status });

    const payload: postReviewPayload = {
      voyageNumber: voyageNo,
      tcCode: tcCode,
      vessel_name: vesselName,
      imo_number: imoNumber,
      status: status,
      comment: comment,
      reviewedBy: reviewedBy,
    };

    setSubmitting(true);

    try {
      const postResult = await postVoyagePnlReviewComments(payload);

      if (postResult === null) {
        log("ApproveRejectCommentBox - handleSubmit - no response");
        setShowError(true);
        return;
      }

      // Clear local memory when APPROVED / REJECTED
      if (tcCode && voyageNo) {
        try {
          cleanupVoyageLocalState(tcCode, voyageNo);
        } catch (e) {
          logE(new Error("ApproveRejectCommentBox - handleSubmit - cleanup failed"));
        }
      }

      log("STATE - handleSubmit - review applied", { status });
      setShowSuccess(true);
    } catch (e) {
      logE(new Error("ApproveRejectCommentBox - handleSubmit - failed"));
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  logV("ApproveRejectCommentBox - BUILD", { submitting, showSuccess });

  const portalChildren = (
    <>
      <div
        className={twMerge("fixed inset-0 z-40 flex items-center justify-center backdrop-blur-sm")}
        onClick={onCancel}
      >
        <div
          className={twMerge(
            "z-50 flex h-[300px] w-[400px] flex-col justify-between rounded-xl border px-3 py-4 shadow-xl",
            theme.uwhcAssistant.border,
            theme.home.tailwind.panelBackground,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Insert comment here..."
            className={twMerge(
              "focus:ring-green-200 mx-3 mb-2 mt-3 h-[200px] rounded border p-2 text-[14px] focus:outline-none focus:ring",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
              theme.home.tailwind.backgound,
            )}
          />

          <div className="mt-4 flex justify-end">
            <ReusableButton width={80} buttonText="Cancel" onClickHandler={onCancel} className="mb-3 mr-3" />

            <ReusableButton width={80} buttonText="Submit" onClickHandler={handleSubmit} className="mb-3" />
          </div>
        </div>
      </div>

      {showError && (
        <BlurredAlert
          message="Submitting review failed, please contact support!"
          onClose={() => {
            setShowError(false);
            onCancel();
          }}
        />
      )}

      {showSuccess && (
        <BlurredAlert
          message="Submitting review successful!"
          onClose={() => {
            setShowSuccess(false);
            onCancel();
            navigate("/");
          }}
        />
      )}
    </>
  );
  return ReactDOM.createPortal(portalChildren, document.body);
};

export default ApproveRejectCommentBox;
